import type { CreateSheetDraft } from './createSheetDraft'
import type { RepairHint } from '../../utils/repairHints'
import type { BankRangeScope } from '../../utils/transactionRanges'

/**
 * The create-sheet flow, parked across a Plaid OAuth redirect.
 *
 * Most banks open Plaid Link in an overlay and the page never unloads. The large
 * ones — Chase, Capital One, Wells Fargo — require their own site, so the browser
 * leaves and comes back through a full page load. Everything the flow holds lives
 * in SideBarView's component state, which does not survive that: the user would
 * return with their bank genuinely connected and the half-built sheet gone.
 *
 * Deliberately narrow. This is not general flow persistence — it is written
 * immediately before Link opens and deleted the moment it is read, so a snapshot
 * can never outlive the round trip it was taken for and resurrect a flow the user
 * had abandoned. Anything longer-lived belongs in the onboarding record, not here.
 *
 * Keyed by uid for the same reason onboardingDraftStorage is: logout() clears
 * sessionStorage, but an account switch in another tab never reaches this one, and
 * a shared key would hand the next user the previous one's draft.
 */
export interface CreateSheetFlowSnapshot {
  draft: CreateSheetDraft
  /** Raw date answers, so stepping back re-seeds the cards the user filled in. */
  dateChoices: Record<string, { option: string; startDate: string; endDate: string }>
  /** The user's shared/per-account choice. Optional for snapshots from older builds. */
  editAllTogether?: boolean
  bankScopes?: Record<string, BankRangeScope>
  /** Which Create New tab to restore when a step returns to that page. */
  createNewTab: 'power-templates' | 'new-spreadsheets' | 'google-drive'
  /**
   * Nicknames given to banks added earlier in this flow, keyed by bank and
   * account together.
   *
   * The draft carries each one as a label, but the account step does not read it
   * from there — it looks the nickname up by key and falls back to the raw Plaid
   * name when the map is empty, then writes that fallback back over the draft on
   * Continue. So a bank named before an OAuth redirect lost its name on return
   * unless the map came back with everything else.
   */
  nicknames: Record<string, string>
  /**
   * The step to return to, when this snapshot was parked by a bank repair rather
   * than by adding a bank.
   *
   * The two detours leave from different places and come back to different ones.
   * Adding a bank always resumes at the account step, which is why that one needs
   * no field. A repair is launched from whichever step was finishing the sheet, and
   * returning to the account step instead would discard the layout and title the
   * user had already given.
   *
   * Its presence is also what tells the restore which detour it is looking at, so
   * a snapshot parked by one cannot be claimed by the other.
   */
  returnStep?: string
  /** Recovery facts learned by the failed fetch, preserved across bank OAuth. */
  repairHints?: RepairHint[]
}

const keyFor = (uid: string) => `v1_create_sheet_flow:${uid}`

/**
 * Park the flow before Plaid Link opens.
 *
 * Called for every connection, not only the ones that redirect, because which
 * kind a bank is only becomes apparent after the user has picked it — by then
 * the page may already be unloading.
 */
export function saveCreateSheetFlow(
  uid: string | undefined,
  snapshot: CreateSheetFlowSnapshot
): void {
  if (!uid) return
  try {
    sessionStorage.setItem(keyFor(uid), JSON.stringify(snapshot))
  } catch {
    // Private mode, or a full quota. The in-page path does not need this at all,
    // and the redirect path degrades to what it did before: the bank connects and
    // the draft is lost. Failing the connection over it would be worse.
  }
}

/**
 * Read the parked flow and delete it in the same breath.
 *
 * Deleting on read rather than on use is what keeps a snapshot from outliving its
 * round trip: a reload that happens for any other reason consumes it once and
 * leaves nothing behind for the visit after.
 */
export function takeCreateSheetFlow(uid: string | undefined): CreateSheetFlowSnapshot | null {
  if (!uid) return null
  try {
    const raw = sessionStorage.getItem(keyFor(uid))
    sessionStorage.removeItem(keyFor(uid))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    // Shape-checked rather than trusted. sessionStorage is writable by anything
    // running on this origin, and a malformed draft reaching the flow would throw
    // somewhere far from here — on the step that reads it, with no clue why.
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !parsed.draft ||
      !Array.isArray(parsed.draft.accounts)
    ) {
      return null
    }
    const repairHints = Array.isArray(parsed.repairHints)
      ? parsed.repairHints.flatMap((value: unknown): RepairHint[] => {
          if (!value || typeof value !== 'object') return []
          const hint = value as Record<string, unknown>
          if (typeof hint.itemId !== 'string' || hint.itemId.length === 0) return []
          if (
            hint.reconnectMode !== undefined &&
            hint.reconnectMode !== 'update' &&
            hint.reconnectMode !== 'relink'
          ) {
            return []
          }
          return [
            {
              itemId: hint.itemId,
              ...(typeof hint.institutionName === 'string'
                ? { institutionName: hint.institutionName }
                : {}),
              ...(hint.reconnectMode ? { reconnectMode: hint.reconnectMode } : {}),
            },
          ]
        })
      : []

    return {
      draft: parsed.draft,
      dateChoices: parsed.dateChoices ?? {},
      ...(parsed.bankScopes && typeof parsed.bankScopes === 'object'
        ? {
            bankScopes: Object.fromEntries(
              Object.entries(
                parsed.bankScopes && typeof parsed.bankScopes === 'object' ? parsed.bankScopes : {}
              ).filter(
                (entry): entry is [string, BankRangeScope] =>
                  entry[1] === 'bank' || entry[1] === 'accounts'
              )
            ),
          }
        : {}),
      ...(typeof parsed.editAllTogether === 'boolean'
        ? { editAllTogether: parsed.editAllTogether }
        : {}),
      createNewTab: parsed.createNewTab ?? 'power-templates',
      // Defaulted, so a snapshot written before this field existed still loads.
      nicknames: parsed.nicknames ?? {},
      // Absent on every add-bank snapshot, which is how the restore tells them
      // apart. Typed before use: sessionStorage is writable by anything on this
      // origin, and a non-string here would be handed to setActiveItem.
      ...(typeof parsed.returnStep === 'string' && parsed.returnStep
        ? { returnStep: parsed.returnStep }
        : {}),
      ...(repairHints.length > 0 ? { repairHints } : {}),
    }
  } catch {
    return null
  }
}

/** Drop a parked flow the user has finished with or walked away from. */
export function clearCreateSheetFlow(uid: string | undefined): void {
  if (!uid) return
  try {
    sessionStorage.removeItem(keyFor(uid))
  } catch {
    // Nothing to do: a snapshot that cannot be removed is consumed on next read.
  }
}

/** Remove a repair fact that a successful Link session has made stale. */
export function clearCreateSheetRepairHint(
  uid: string | undefined,
  repairedItemId: string | undefined
): void {
  if (!uid || !repairedItemId) return
  try {
    const key = keyFor(uid)
    const raw = sessionStorage.getItem(key)
    if (!raw) return
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.repairHints)) return
    const repairHints = parsed.repairHints.filter(
      (hint: unknown) =>
        !hint ||
        typeof hint !== 'object' ||
        (hint as Record<string, unknown>).itemId !== repairedItemId
    )
    sessionStorage.setItem(key, JSON.stringify({ ...parsed, repairHints }))
  } catch {
    // Recovery state is best-effort. The live bank status remains authoritative.
  }
}

/**
 * Which detour a restored snapshot belongs to, if any.
 *
 * One read serves both, because takeCreateSheetFlow deletes on read: whichever
 * effect looks first consumes it, so the decision has to be made in one place or
 * the second case silently never restores.
 *
 * `returnStep` is the discriminator, not the view the browser came back to. Both
 * detours can return to a dashboard view that the user could equally have reached
 * from the sidebar, so the view alone cannot say whether a snapshot was meant for
 * this arrival — only the snapshot can.
 *
 * Returns null when there is nothing to do, which includes the ordinary case of a
 * reload that is not a return from anywhere. A snapshot that does not match its
 * arrival is still consumed by the read and deliberately not applied: it belongs
 * to a round trip that did not happen, and restoring it would resurrect a flow the
 * user left behind.
 */
export type RestoreDestination =
  | { kind: 'resume-add-bank' }
  | { kind: 'resume-after-repair'; returnStep: string }

export const restoreDestinationFor = (
  snapshot: CreateSheetFlowSnapshot | null,
  activeView: string,
  addBankReturnView: string
): RestoreDestination | null => {
  if (!snapshot) return null
  if (snapshot.returnStep) {
    // Parked by a bank repair. It returns to Banks & Accounts, whose view is the
    // one the reconnect redirect names.
    return activeView === 'bank'
      ? { kind: 'resume-after-repair', returnStep: snapshot.returnStep }
      : null
  }
  return activeView === addBankReturnView ? { kind: 'resume-add-bank' } : null
}
